import React from "react";
import { Box, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Category";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import PageHeader from "../../components/PageHeader";
import Actions from "../../components/Actions";
import ConfirmDialog from "../../components/ConfirmDialog";
import ClassFormDialog from "../../components/classes/ClassFormDialog";
import ClassCategoriesDialog from "../../components/classes/ClassCategoriesDialog";
import ClassSessionsDialog from "../../components/classes/ClassSessionsDialog";
import ClassEnrollDialog from "../../components/classes/ClassEnrollDialog";
import RescheduleClassDialog from "../../components/classes/RescheduleClassDialog";
import EndClassSeriesDialog from "../../components/classes/EndClassSeriesDialog";
import { ClassDefinition } from "../../components/classes/types";
import {
  ENROLLMENT_MODE_OPTIONS,
  formatMoney,
  isOpenEndedClass,
  isRecurringClass,
  labelForValue,
  toActiveLabel,
} from "../../components/classes/classUtils";
import {
  useCategoryOptions,
  useCoachOptions,
  useUnitOptions,
} from "../../components/classes/useClassOptions";
import { deleteClass, getAllClasses } from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { IAction } from "../../types/table/interfaces.type";

type ClassRow = {
  id: string;
  name: string;
  category: string;
  coach: string;
  unit: string;
  capacity: string;
  price: string;
  enrollment_mode: string;
  schedule: string;
  active: string;
  raw: ClassDefinition;
};

const enrollmentOptions = ENROLLMENT_MODE_OPTIONS.map((o) => ({
  label: o.label,
  value: String(o.value),
}));

const Classes: React.FC = () => {
  const params = useParams<{ businessId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const businessId = params.businessId;

  const [rows, setRows] = React.useState<ClassRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [unitFilter, setUnitFilter] = React.useState<string>("");
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [categoryToken, setCategoryToken] = React.useState(0);

  const [formOpen, setFormOpen] = React.useState(false);
  const [editingClass, setEditingClass] = React.useState<ClassDefinition | null>(
    null
  );
  const [categoriesOpen, setCategoriesOpen] = React.useState(false);
  const [sessionsClass, setSessionsClass] = React.useState<ClassDefinition | null>(
    null
  );
  const [enrollClass, setEnrollClass] = React.useState<ClassDefinition | null>(null);
  const [rescheduleClass, setRescheduleClass] =
    React.useState<ClassDefinition | null>(null);
  const [endSeriesClass, setEndSeriesClass] =
    React.useState<ClassDefinition | null>(null);
  const [deleteClassTarget, setDeleteClassTarget] =
    React.useState<ClassDefinition | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const { options: unitOptions } = useUnitOptions(businessId);
  const { options: coachOptions } = useCoachOptions();
  const { options: categoryOptions } = useCategoryOptions(true, categoryToken);

  React.useEffect(() => {
    let cancelled = false;

    const fetchClasses = async () => {
      setLoading(true);
      try {
        const classes = await getAllClasses(unitFilter || undefined);
        const mapped: ClassRow[] = classes.map((item: any) => ({
          id: String(item.id),
          name: item.name || "-",
          category: item.category?.name || "-",
          coach: item.coach?.full_name || "-",
          unit: item.unit?.name || "-",
          capacity: String(item.capacity ?? "-"),
          price: formatMoney(item.price),
          enrollment_mode: labelForValue(enrollmentOptions, item.enrollment_mode),
          // An open-ended class has no last session to show, so the column
          // says how it ends rather than when.
          schedule: !isRecurringClass(item)
            ? "One-off"
            : isOpenEndedClass(item)
            ? "Until further notice"
            : item.recurrence_end
            ? `Until ${String(item.recurrence_end).slice(0, 10)}`
            : `${item.recurrence_count} sessions`,
          active: toActiveLabel(item.active),
          raw: item,
        }));
        if (!cancelled) setRows(mapped);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to fetch classes."));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchClasses();
    return () => {
      cancelled = true;
    };
  }, [refetchTrigger, unitFilter]);

  const findClass = (id: string | number) =>
    rows.find((row) => String(row.id) === String(id))?.raw || null;

  // An enrollment-confirmation push carries screen: "ClassDetails" and a
  // class_id, which the service worker turns into ?classId=<id> here. Waits for
  // the row to load so the sessions dialog opens with a real class rather than
  // null, then drops the param so a refresh doesn't reopen it.
  React.useEffect(() => {
    const deepLinkedClass = new URLSearchParams(location.search).get("classId");
    if (!deepLinkedClass || rows.length === 0) return;
    const target = findClass(deepLinkedClass);
    if (target) setSessionsClass(target);

    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("classId");
    navigate(
      { pathname: location.pathname, search: nextParams.toString() },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, navigate, rows]);

  const sessionsAction: IAction<any> = {
    id: "sessions",
    label: "Sessions",
    name: "Sessions",
    icon: (
      <Tooltip title="Sessions & membership clients">
        <EventNoteIcon sx={{ color: COLORS.MainColor, width: 23 }} />
      </Tooltip>
    ),
    handler: ({ id }: any) => setSessionsClass(findClass(id)),
  };

  const enrollAction: IAction<any> = {
    id: "enroll",
    label: "Enroll",
    name: "Enroll",
    icon: (
      <Tooltip title="Enroll a client (buys a block of sessions)">
        <PersonAddIcon sx={{ color: "#2e7d32", width: 23 }} />
      </Tooltip>
    ),
    handler: ({ id }: any) => setEnrollClass(findClass(id)),
    // Drop-in only classes have no block to sell.
    visible: (row: any) => row?.raw?.enrollment_mode !== "DROP_IN",
  };

  // Moving a class in time is separate from editing its name and price: it
  // re-projects the unbooked dates and leaves the booked ones behind, which is
  // a different enough operation to deserve its own action.
  const rescheduleAction: IAction<any> = {
    id: "reschedule",
    label: "Reschedule",
    name: "Reschedule",
    icon: (
      <Tooltip title="Change when this class runs">
        <EditCalendarIcon sx={{ color: "#0288d1", width: 23 }} />
      </Tooltip>
    ),
    handler: ({ id }: any) => setRescheduleClass(findClass(id)),
  };

  // Only recurring classes have a series to stop; a one-off is just cancelled.
  const endSeriesAction: IAction<any> = {
    id: "end-series",
    label: "End",
    name: "End",
    icon: (
      <Tooltip title="Stop this class after a date">
        <EventRepeatIcon sx={{ color: "#d32f2f", width: 23 }} />
      </Tooltip>
    ),
    handler: ({ id }: any) => setEndSeriesClass(findClass(id)),
    visible: (row: any) => isRecurringClass(row?.raw),
  };

  const handleDeleteConfirm = async () => {
    if (!deleteClassTarget) return;
    setDeleting(true);
    try {
      await deleteClass(String(deleteClassTarget.id));
      toast.success("Class deleted.");
      setDeleteClassTarget(null);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error) {
      // The backend 400s with a clear reason (e.g. "end the class first" /
      // "cancel upcoming sessions first") — surface it to the admin verbatim.
      toast.error(getErrorMessage(error, "Failed to delete class."));
    } finally {
      setDeleting(false);
    }
  };

  const actions = [
    ...Actions({
      handleOpenEditModal: {
        handler: (id) => {
          setEditingClass(findClass(id));
          setFormOpen(true);
        },
      },
      onDelete: {
        handler: (id) => setDeleteClassTarget(findClass(id)),
      },
    }),
    sessionsAction,
    enrollAction,
    rescheduleAction,
    endSeriesAction,
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Classes"
          subtitle="Your classes and their schedules."
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          selectConfig={{
            options: unitOptions,
            value: unitFilter,
            onChange: (v: any) => {
              const isEmpty =
                v === "" || v === null || (Array.isArray(v) && v.length === 0);
              setUnitFilter(isEmpty ? "" : String(v));
            },
            label: "Filter by Unit",
            placeholder: "All units",
            size: "medium",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: { backgroundColor: COLORS.WhiteBackground },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              label: "Add Class",
              onClick: () => {
                setEditingClass(null);
                setFormOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              size: "small",
              variant: "outlined",
              label: "Categories",
              startIcon: <CategoryIcon />,
              onClick: () => setCategoriesOpen(true),
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<ClassRow>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination
          loading={loading}
          fields={[
            { key: "name", header: "Class" },
            { key: "coach", header: "Coach" },
            { key: "category", header: "Category" },
            { key: "unit", header: "Unit" },
            { key: "capacity", header: "Capacity" },
            { key: "price", header: "Drop-in Price" },
            { key: "enrollment_mode", header: "Enrollment" },
            { key: "schedule", header: "Runs" },
            { key: "active", header: "Status" },
          ]}
          actions={actions}
          fetchFunc={async (tableParams?: any) => {
            const query = String((tableParams && tableParams.search) || search || "")
              .toLowerCase()
              .trim();

            const content = rows.filter((row) => {
              if (!query) return true;
              return [row.name, row.coach, row.category, row.unit, row.schedule, row.active]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase())
                .join(" ")
                .includes(query);
            });

            const pageSize = (tableParams && tableParams.size) || 10;
            return {
              content,
              number: 0,
              size: pageSize,
              totalElements: content.length,
              totalPages: Math.max(1, Math.ceil(content.length / pageSize)),
            };
          }}
        />

        <ClassFormDialog
          open={formOpen}
          classItem={editingClass}
          unitOptions={unitOptions}
          coachOptions={coachOptions}
          categoryOptions={categoryOptions}
          defaultUnitId={unitFilter}
          onClose={() => {
            setFormOpen(false);
            setEditingClass(null);
          }}
          onSaved={() => setRefetchTrigger((prev) => prev + 1)}
        />

        <ClassCategoriesDialog
          open={categoriesOpen}
          onClose={() => setCategoriesOpen(false)}
          onChanged={() => {
            setCategoryToken((prev) => prev + 1);
            setRefetchTrigger((prev) => prev + 1);
          }}
        />

        <ClassSessionsDialog
          open={Boolean(sessionsClass)}
          businessId={businessId}
          classItem={sessionsClass}
          onClose={() => setSessionsClass(null)}
        />

        <ClassEnrollDialog
          open={Boolean(enrollClass)}
          businessId={businessId}
          classItem={enrollClass}
          onClose={() => setEnrollClass(null)}
          onEnrolled={() => setRefetchTrigger((prev) => prev + 1)}
        />

        <RescheduleClassDialog
          open={Boolean(rescheduleClass)}
          classItem={rescheduleClass}
          onClose={() => setRescheduleClass(null)}
          onRescheduled={() => setRefetchTrigger((prev) => prev + 1)}
        />

        <EndClassSeriesDialog
          open={Boolean(endSeriesClass)}
          classItem={endSeriesClass}
          onClose={() => setEndSeriesClass(null)}
          onEnded={() => setRefetchTrigger((prev) => prev + 1)}
        />

        <ConfirmDialog
          open={Boolean(deleteClassTarget)}
          onClose={() => {
            if (!deleting) setDeleteClassTarget(null);
          }}
          title="Delete class"
          message={`Are you sure you want to delete "${
            deleteClassTarget?.name || "this class"
          }"? This action cannot be undone.`}
          confirmButtonText={deleting ? "Deleting..." : "Delete"}
          onConfirm={handleDeleteConfirm}
        />
      </Box>
    </Box>
  );
};

export default Classes;
